import { useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { TreeDragDropHandlers, useTreeDragDrop } from '@futrli/ui-components/components/draggable-tree-grid';

import { COA_TYPE, COADisplayAccount } from '../types';
import { findAndAddNode, findAndRemoveNode, findNode, findParentNode, getChildIds } from '../utils';

type MoveBoundary = 'top' | 'bottom';

interface MoveToBoundaryPosition {
  parent: COADisplayAccount;
  sourceIndex: number;
  targetIndex: number;
}

/**
 * Resolves the current and destination positions for a parent-aware boundary move.
 * Nested rows target the requested edge of their parent group, while root rows target the
 * requested edge of their category in the shared root tree.
 *
 * @param tree Current chart of accounts tree.
 * @param item Row that may be moved.
 * @param boundary Parent or category edge that the row should move to.
 * @returns The parent and sibling indexes required for the move, or undefined when the row cannot be located.
 */
const getMoveToBoundaryPosition = (
  tree: COADisplayAccount,
  item: COADisplayAccount,
  boundary: MoveBoundary
): MoveToBoundaryPosition | undefined => {
  const parent = findParentNode(tree, item.id);
  if (!parent) {
    return;
  }

  const sourceIndex = parent.children.findIndex(child => child.id === item.id);
  let targetIndex = boundary === 'top' ? 0 : parent.children.length - 1;

  if (parent.id === tree.id) {
    const categoryIndexes = parent.children.reduce<number[]>((indexes, child, index) => {
      if (child.category === item.category) {
        indexes.push(index);
      }
      return indexes;
    }, []);
    targetIndex = boundary === 'top' ? categoryIndexes[0] : categoryIndexes[categoryIndexes.length - 1];
  }

  if (sourceIndex < 0 || typeof targetIndex !== 'number' || targetIndex < 0) {
    return;
  }

  return { parent, sourceIndex, targetIndex };
};

/** Returns whether moving a row to the requested boundary would change its position. */
const canMoveToBoundary = (position: MoveToBoundaryPosition | undefined, boundary: MoveBoundary) =>
  !!position &&
  (boundary === 'top' ? position.sourceIndex > position.targetIndex : position.sourceIndex < position.targetIndex);

const applyManualMoveUp = (
  dragItem: COADisplayAccount,
  dropItem: COADisplayAccount,
  nodeToBeMoved: COADisplayAccount,
  tree: COADisplayAccount
): COADisplayAccount | undefined => {
  const dragParentId = dragItem.parentId;
  const dropParentId = dropItem.parentId;

  if (dragParentId && dragParentId !== dropParentId) {
    // Moving up out of a group → place before the group in its parent
    const groupParent = findParentNode(tree, dragParentId);
    if (groupParent) {
      const groupIndex = groupParent.children.findIndex(c => c.id === dragParentId);
      groupParent.children.splice(groupIndex, 0, nodeToBeMoved);
      return tree;
    }
  } else if (dropItem.type === 'group' && dragItem.type !== 'group') {
    // Moving up onto a group from outside → enter as last child
    const targetGroup = findNode(dropItem.id, tree);
    if (targetGroup) {
      targetGroup.children.push(nodeToBeMoved);
      return tree;
    }
  } else if (dropParentId && dropParentId !== dragParentId) {
    // Moving up into a group → enter as last child
    const targetGroup = findNode(dropParentId, tree);
    if (targetGroup) {
      targetGroup.children.push(nodeToBeMoved);
      return tree;
    }
  }
  return undefined;
};

const applyManualMoveDown = (
  dragItem: COADisplayAccount,
  dropItem: COADisplayAccount,
  nodeToBeMoved: COADisplayAccount,
  tree: COADisplayAccount
): COADisplayAccount | undefined => {
  const dragParentId = dragItem.parentId;
  const dropParentId = dropItem.parentId;

  if (dragParentId && dragParentId !== dropParentId) {
    // Moving down out of a group → place after the group in its parent
    const groupParent = findParentNode(tree, dragParentId);
    if (groupParent) {
      const groupIndex = groupParent.children.findIndex(c => c.id === dragParentId);
      groupParent.children.splice(groupIndex + 1, 0, nodeToBeMoved);
      return tree;
    }
  } else if (dropItem.type === 'group' && dragItem.type !== 'group') {
    // Moving down onto a group from outside → enter as first child
    const targetGroup = findNode(dropItem.id, tree);
    if (targetGroup) {
      targetGroup.children.unshift(nodeToBeMoved);
      return tree;
    }
  }
  return undefined;
};

const applyManualMove = (
  direction: 'up' | 'down',
  dragItem: COADisplayAccount,
  dropItem: COADisplayAccount,
  nodeToBeMoved: COADisplayAccount,
  tree: COADisplayAccount
): COADisplayAccount | undefined => {
  if (direction === 'up') {
    return applyManualMoveUp(dragItem, dropItem, nodeToBeMoved, tree);
  }
  return applyManualMoveDown(dragItem, dropItem, nodeToBeMoved, tree);
};

interface UseCategoryDragDropParams {
  data: COADisplayAccount[];
  categoryName: string;
  accountsTree: COADisplayAccount | null;
  nodeIdsWithParentIds: Map<string, string[]>;
  onDragAndDropUpdate: (tree: COADisplayAccount) => void;
  onManualMoveUpdate: (tree: COADisplayAccount, coaType: COA_TYPE) => Promise<void>;
  isUpdatingStructure: boolean;
  isBalanceSheet: boolean;
}

export interface DragHandlers extends TreeDragDropHandlers {
  /** Returns whether the row has a later sibling within its current group or category. */
  canMoveToBottom: (index: number) => boolean;
  /** Returns whether the row has an earlier sibling within its current group or category. */
  canMoveToTop: (index: number) => boolean;
}

/**
 * Encapsulates all drag-and-drop logic for reordering rows within a COA category section.
 *
 * Manages drag/drop refs, visual indicators (background highlights on drag-over),
 * and the tree update logic when a row is dropped into a new position.
 * Validates moves: prevents dragging to self, to own children, to parent group, or across categories.
 */
export const useCategoryDragDrop = ({
  data,
  accountsTree,
  nodeIdsWithParentIds,
  onDragAndDropUpdate,
  isUpdatingStructure,
  onManualMoveUpdate,
  isBalanceSheet
}: UseCategoryDragDropParams) => {
  const isDragInvalid = useCallback(
    (dragIndex: number, overIndex: number) => {
      const dragContent = data[dragIndex];
      const overContent = data[overIndex];

      if (!dragContent || !overContent) {
        return true;
      }
      if (dragContent.id === overContent.id) {
        return true;
      }
      if (dragContent.type === 'group') {
        const childIds = getChildIds(dragContent.children);
        if (childIds.includes(overContent.id)) {
          return true;
        }
      }
      return false;
    },
    [data]
  );

  const calculateNewTree = useCallback(
    (dragIndex: number, dropIndex: number, direction?: 'up' | 'down'): COADisplayAccount | undefined => {
      const dragItemContent = data[dragIndex];
      const dropItemContent = data[dropIndex];

      if (!dragItemContent || !dropItemContent || isDragInvalid(dragIndex, dropIndex)) {
        return;
      }
      if (dragItemContent.category !== dropItemContent.category) {
        return;
      }

      const copiedAccountsTree = cloneDeep(accountsTree);
      const treeArg = copiedAccountsTree || undefined;
      const nodeToBeMoved = findNode(dragItemContent.id, treeArg);
      const updatedTree = findAndRemoveNode(dragItemContent.id, treeArg);

      if (!nodeToBeMoved || !updatedTree) {
        return;
      }

      if (direction) {
        const result = applyManualMove(direction, dragItemContent, dropItemContent, nodeToBeMoved, updatedTree);
        if (result) {
          return result;
        }
      }

      // Default: use findAndAddNode for drag-and-drop and same-parent manual moves
      return findAndAddNode(
        updatedTree,
        dropItemContent.id,
        nodeToBeMoved,
        dragIndex,
        dropIndex,
        data,
        nodeIdsWithParentIds
      );
    },
    [data, accountsTree, nodeIdsWithParentIds, isDragInvalid]
  );

  const handleManualMove = useCallback(
    (tree: COADisplayAccount) => {
      onManualMoveUpdate(tree, isBalanceSheet ? COA_TYPE.BALANCE_SHEET : COA_TYPE.PROFIT_AND_LOSS);
    },
    [onManualMoveUpdate, isBalanceSheet]
  );

  const canMoveToTreeBoundary = useCallback(
    (index: number, boundary: MoveBoundary) => {
      const item = data[index];
      if (!accountsTree || !item) {
        return false;
      }

      return canMoveToBoundary(getMoveToBoundaryPosition(accountsTree, item, boundary), boundary);
    },
    [accountsTree, data]
  );

  /** Determines whether move-to-top would change the row's position without changing its parent. */
  const canMoveToTop = useCallback((index: number) => canMoveToTreeBoundary(index, 'top'), [canMoveToTreeBoundary]);

  /** Determines whether move-to-bottom would change the row's position without changing its parent. */
  const canMoveToBottom = useCallback((index: number) => canMoveToTreeBoundary(index, 'bottom'), [
    canMoveToTreeBoundary
  ]);

  /**
   * Moves a row to an edge of its parent group or category while preserving the node's
   * descendants and group membership.
   */
  const moveToTreeBoundary = useCallback(
    (index: number, boundary: MoveBoundary) => {
      const item = data[index];
      if (isUpdatingStructure || !accountsTree || !item) {
        return;
      }

      const currentPosition = getMoveToBoundaryPosition(accountsTree, item, boundary);
      if (!canMoveToBoundary(currentPosition, boundary)) {
        return;
      }

      const copiedAccountsTree = cloneDeep(accountsTree);
      const position = getMoveToBoundaryPosition(copiedAccountsTree, item, boundary);
      if (!position) {
        return;
      }

      const [nodeToBeMoved] = position.parent.children.splice(position.sourceIndex, 1);
      if (!nodeToBeMoved) {
        return;
      }
      position.parent.children.splice(position.targetIndex, 0, nodeToBeMoved);
      handleManualMove(copiedAccountsTree);
    },
    [accountsTree, data, handleManualMove, isUpdatingStructure]
  );

  /** Moves a row to the beginning of its current group or category. */
  const onMoveToTop = useCallback((index: number) => moveToTreeBoundary(index, 'top'), [moveToTreeBoundary]);

  /** Moves a row to the end of its current group or category. */
  const onMoveToBottom = useCallback((index: number) => moveToTreeBoundary(index, 'bottom'), [moveToTreeBoundary]);

  const dragHandlers = useTreeDragDrop<COADisplayAccount>({
    dataLength: data.length,
    isDragInvalid,
    calculateNewTree,
    onDragComplete: onDragAndDropUpdate,
    onManualMoveComplete: handleManualMove,
    canManualMove: !isUpdatingStructure
  });

  return { ...dragHandlers, canMoveToBottom, canMoveToTop, onMoveToBottom, onMoveToTop };
};
